import { Observable, forkJoin } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { Capability } from '../permissions/capability.constants';
import { UserInfo } from '../types/user.types';
import { FeatureFlag } from './constants';
import { FeatureFlagService } from './feature-flag.service';

type FlagReader = Pick<FeatureFlagService, 'getBooleanFlag'>;

type CapabilityReader = {
  has(capability: Capability): boolean;
};

/**
 * Interim One A&R actions gate.
 * TODO(CSP-26759): switch to (ROLLOUT_ANR_ACTIONS_EARLY_ACCESS && earlyAccess) || ROLLOUT_ANR_ACTIONS
 */
export function canShowAnrActions(
  featureFlagService: FlagReader,
  userInfo: UserInfo
): boolean {
  return (
    featureFlagService.getBooleanFlag(
      FeatureFlag.ROLLOUT_ONE_ANALYSIS_RESPONSE
    ) === true && !!userInfo.adminPreferences?.earlyAccess
  );
}

/**
 * Interim converged Overview gate.
 * TODO(CSP-26759): keep this as ROLLOUT_ONE_ANALYSIS_RESPONSE && canShowAnrActions
 * after showAnrActions switches to the actions flags.
 */
export function canShowConvergedOverview(
  featureFlagService: FlagReader,
  userInfo: UserInfo
): boolean {
  return (
    featureFlagService.getBooleanFlag(
      FeatureFlag.ROLLOUT_ONE_ANALYSIS_RESPONSE
    ) === true && canShowAnrActions(featureFlagService, userInfo)
  );
}

export function isConvergedCg(
  featureFlagService: FlagReader,
  userInfo: UserInfo
): boolean {
  return (
    canShowAnrActions(featureFlagService, userInfo) &&
    !userInfo.capsAndPermissions.has(
      Capability.APPLICATION_ANALYSIS_AND_RESPONSE_GWL_API
    )
  );
}

export function hasGwlAccess(
  gwlAnalysisResponse: boolean,
  gwlPhishingPilot: boolean,
  capsAndPermissions: CapabilityReader
): boolean {
  return (
    (gwlAnalysisResponse || gwlPhishingPilot) &&
    capsAndPermissions.has(Capability.APPLICATION_ANALYSIS_AND_RESPONSE_GWL_API)
  );
}

/** Waits until LaunchDarkly is ready, then returns the GWL flags and the user. */
export function whenAnrRoutingContext(
  featureFlagService: Pick<
    FeatureFlagService,
    'getPlatformBooleanFlag$' | 'whenReady'
  >,
  userService: { getUserInfo(): Observable<UserInfo> }
): Observable<{
  gwlAnalysisResponse: boolean;
  gwlPhishingPilot: boolean;
  userInfo: UserInfo;
}> {
  return forkJoin({
    gwlAnalysisResponse: featureFlagService.getPlatformBooleanFlag$(
      FeatureFlag.ROLLOUT_GWL_ANALYSIS_RESPONSE
    ),
    gwlPhishingPilot: featureFlagService.getPlatformBooleanFlag$(
      FeatureFlag.ROLLOUT_GWL_PHISHING_PILOT
    ),
    flagsReady: featureFlagService.whenReady().pipe(take(1)),
    userInfo: userService.getUserInfo().pipe(take(1)),
  }).pipe(
    map(({ gwlAnalysisResponse, gwlPhishingPilot, userInfo }) => ({
      gwlAnalysisResponse,
      gwlPhishingPilot,
      userInfo,
    }))
  );
}
